import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import { useDisclosure, useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import { Button, Input, Select, Tooltip } from 'antd';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { useQuestReportWorkOrderWorkshop } from '@/core/domain/questReport/WorkOrder/WorkOder.hooks';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { buildParamsStr } from '@/utils/queryParams';
import WorkOrderReportAdministrativeModal from '../../../WorkOrderReportAdministrativeModal';
import WorkOrderReportQuotationMapModal from '../../../WorkOrderReportQuotationMapModal';
import WorkOrderReportAdministrativeSyntheticModal from '../../../WorkOrderReportAdministrativeSyntheticModal';
import WorkOrderReportWorkOrderCostModal from '../../../WorkOrderReportWorkOrderCostModal';

export default function WorkOrderReportsButton() {
  const [workOrderReportType, setWorkOrderReportType] = useState<string>();

  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();

  const report = useReportGenerate();

  const [format, setFormat] = useState<FileType>();

  const [modalQuotationMapOpened, modalQuotationMapAction] = useDisclosure();

  const [modalAdministrativeOpened, modalAdministrativeAction] =
    useDisclosure();

  const [
    modalAdministrativeSyntheticOpened,
    modalAdministrativeSyntheticAction,
  ] = useDisclosure();

  const [modalWorkOrderCostOpened, modalWorkOrderCostAction] = useDisclosure();

  const workshopReport = useQuestReportWorkOrderWorkshop();

  const {
    workorder: { data: workorder },
    complement: { data: complement, complementIdentifier },
  } = useWorkorderDetailContext();

  const getWorkOrderType = () => {
    return '/generate/' + workOrderReportType;
  };

  const reportTypeString =
    workOrderReportType === 'work-order/workshop'
      ? 'Orçamento para oficina'
      : workOrderReportType === 'work-order-synthetic'
      ? 'Administrativo - sintético'
      : 'Lista de inspeção';

  const generateReport = useCommonGenerateReport(
    getWorkOrderType(),
    'Relatório gerado com sucesso!',
    `Relatório de ${reportTypeString} gerado com sucesso.`,
  );

  const reportAdminSynthetic = useCommonGenerateReport(
    ``,
    'Relatório gerado com sucesso!',
    'Relatório administrativo - Sintético gerado com sucesso.',
  );

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      if (workorder && complement) {
        if (workOrderReportType === 'work-order/workshop') {
          const result = await workshopReport.mutateAsync({
            complementId: complement.id,
            header: true,
          });

          if (callback) {
            callback(result);
          }
        } else if (workOrderReportType === 'work-order/workshopSynthetic') {
          const result = await workshopReport.mutateAsync({
            complementId: complement.id,
            header: false,
          });

          if (callback) {
            callback(result);
          }
        } else if (
          workOrderReportType === 'work-order-synthetic' &&
          complement
        ) {
          setFormat(format);

          const permission = hasPermissions([
            Authorities.MOVIMENTATION_WORKORDER_VIEW_COST_HOUR_ADMINISTRATIVE_BUDGET_REPORTS,
          ]);

          if (permission) {
            modalAdministrativeSyntheticAction.toggle();
          } else if (workorder?.id) {
            const fileName = `Relatório Administrativo-sintético.pdf`;

            const result = await reportAdminSynthetic.mutateAsync({
              workOrderId: workorder.id,
              complementId: complement?.id,
              format: 'PDF',
              url: '/generate/work-order-synthetic',
            });

            const action =
              format === 'PDF' ? report.downloadFile : report.openFile;

            action({
              result,
              fileName,
              fileType: 'application/pdf',
            });
          }
        } else if (workOrderReportType === 'work-order/inspection-list') {
          const obj = {
            workorderId: workorder.id,
          };
          const result = await generateReport.mutateAsync(obj);
          if (callback) {
            callback(result);
          }
        } else if (workOrderReportType === 'work-order/quotationMap') {
          setFormat(format);

          modalQuotationMapAction.open();
        } else if (workOrderReportType === 'work-order/administrative') {
          setFormat(format);

          modalAdministrativeAction.open();
        } else if (workOrderReportType === 'work-order/cost' && complement) {
          modalWorkOrderCostAction.toggle();
        } else if (workOrderReportType === 'work-order/mechanics-record') {
          const linkRedirect = `/privateReports/mechanicsRecord/${
            workorder.company.id
          }?${buildParamsStr({
            workOrderId: workorder.id,
            complementId: complement.id,
            isSynthetic: false,
            print: format === 'PDF',
          })}`;

          window.open(linkRedirect, '_blank');
        } else if (
          workOrderReportType === 'work-order/mechanics-record-synthetic'
        ) {
          const linkRedirect = `/privateReports/mechanicsRecord/${
            workorder.company.id
          }?${buildParamsStr({
            workOrderId: workorder.id,
            complementId: complement.id,
            isSynthetic: true,
            print: format === 'PDF',
          })}`;

          window.open(linkRedirect, '_blank');
        } else if (workOrderReportType === 'work-order/inspection-record') {
          const linkRedirect = `/privateReports/inspectionRecord/${
            workorder.company.id
          }?${buildParamsStr({
            workOrderId: workorder.id,
            print: format === 'PDF',
          })}`;

          window.open(linkRedirect, '_blank');
        }
      }
    } catch {}
    setFileUnloading(format);
  };

  const workOrderReportOptions = [
    {
      label: 'Relatório para Oficina',
      value: 'work-order/workshop',
      disabled: !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_PRINT_REPORT_FOR_WORKSHOP,
        Authorities.MOVIMENTATION_WORKORDER_PRINT,
      ]),
    },
    {
      label: 'Relatório para Oficina Sintético',
      value: 'work-order/workshopSynthetic',
      disabled: !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_PRINT_REPORT_FOR_WORKSHOP,
        Authorities.MOVIMENTATION_WORKORDER_PRINT,
      ]),
    },
    {
      label: 'Lista de inspeção',
      value: 'work-order/inspection-list',
      disabled: !hasPermissions([Authorities.MOVIMENTATION_WORKORDER_PRINT]),
    },
    {
      label: 'Mapa de cotação',
      value: 'work-order/quotationMap',
      disabled: !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_PRINT_QUOTATION_MAP,
        Authorities.MOVIMENTATION_WORKORDER_PRINT,
      ]),
    },
    {
      label: 'Custo da OS',
      value: 'work-order/cost',
      disabled: !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_PRINT_REPORT_OF_COST,
        Authorities.MOVIMENTATION_WORKORDER_PRINT,
      ]),
    },
    {
      label: 'Relatório Administrativo',
      value: 'work-order/administrative',
      disabled: !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_PRINT_ADMINISTRATIVE_REPORT,
        Authorities.MOVIMENTATION_WORKORDER_PRINT,
      ]),
    },
    {
      label: 'Relatório administrativo - sintético',
      value: 'work-order-synthetic',
      disabled: !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_PRINT_ADMINISTRATIVE_REPORT,
        Authorities.MOVIMENTATION_WORKORDER_PRINT,
      ]),
    },
    {
      label: 'Folha do mecânico',
      value: 'work-order/mechanics-record',
    },
    {
      label: 'Folha do mecânico sintética',
      value: 'work-order/mechanics-record-synthetic',
    },
    {
      label: 'Ficha de inspeção técnica',
      value: 'work-order/inspection-record',
    },
  ];

  const defaultReport = useMemo(() => {
    const reportsEnabled =
      workOrderReportOptions.filter((option) => !option.disabled) || [];

    let value = reportsEnabled?.[0]?.value;

    if (
      reportsEnabled.find(
        (report) => report.value === 'work-order/administrative',
      )
    ) {
      value = 'work-order/administrative';
    }

    return value;
  }, [workOrderReportOptions]);

  useEffect(() => {
    if (!workOrderReportType) {
      setWorkOrderReportType(defaultReport);
    }
  }, [defaultReport]);

  function getFileName(value: string, type: string) {
    const option = workOrderReportOptions.find((item) => item.value === value)
      ?.label as string;

    return `${option.replaceAll(' ', '_')}_${moment().format(
      'DD_MM_YYYY',
    )}.${type.toLowerCase()}`;
  }

  const isDisabledButtons = useMemo(
    () =>
      workOrderReportOptions.every(({ disabled }) => disabled) ||
      workOrderReportOptions.find((item) => item.value === defaultReport)
        ?.disabled,
    [defaultReport],
  );

  return (
    <>
      <Input.Group
        compact
        style={{
          flexWrap: 'nowrap',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Select
          defaultValue={defaultReport}
          options={workOrderReportOptions}
          onChange={setWorkOrderReportType}
          style={{ width: '100%', overflow: 'hidden' }}
          disabled={workOrderReportOptions.every(({ disabled }) => disabled)}
          placeholder="Relatórios"
          placement="topLeft"
        />

        <Button
          type="primary"
          icon={
            <Tooltip title="Gerar PDF">
              <DownloadOutlined />
            </Tooltip>
          }
          key="PDF"
          onClick={() => {
            handleGenerateReport('PDF', (result) =>
              report.downloadFile({
                result,
                fileName: workOrderReportType
                  ? getFileName(workOrderReportType, 'pdf')
                  : '',
              }),
            );
          }}
          loading={fileLoading.PDF}
          disabled={isDisabledButtons}
          style={{ minWidth: '30px' }}
        />

        <Button
          type="primary"
          icon={
            <Tooltip title="Visualizar/Imprimir">
              <EyeOutlined />
            </Tooltip>
          }
          onClick={() => {
            handleGenerateReport('REPORT_VIEW', (result) =>
              report.openFile({
                result,
                fileName: workOrderReportType
                  ? getFileName(workOrderReportType, 'pdf')
                  : '',
                fileType: 'application/pdf',
              }),
            );
          }}
          key="viewReport"
          loading={fileLoading.REPORT_VIEW}
          disabled={isDisabledButtons}
          style={{ minWidth: '30px' }}
        />
      </Input.Group>

      <WorkOrderReportQuotationMapModal
        format={format}
        open={modalQuotationMapOpened}
        onCancel={modalQuotationMapAction.toggle}
        workOrderId={workorder?.id}
        complementNumber={complement?.number}
      />

      <WorkOrderReportAdministrativeModal
        format={format}
        open={modalAdministrativeOpened}
        onCancel={modalAdministrativeAction.close}
        workOrderId={workorder?.id}
        complementNumber={complement?.number}
        licensePlate={workorder?.vehicle?.licensePlate}
        complementIdentifier={complementIdentifier}
      />

      <WorkOrderReportAdministrativeSyntheticModal
        format={format}
        open={modalAdministrativeSyntheticOpened}
        onCancel={modalAdministrativeSyntheticAction.close}
        workOrderId={workorder?.id}
        complementId={complement?.id}
        licensePlate={workorder?.vehicle?.licensePlate}
        complementIdentifier={complementIdentifier}
      />

      <WorkOrderReportWorkOrderCostModal
        format={format}
        open={modalWorkOrderCostOpened}
        onCancel={modalWorkOrderCostAction.close}
        workOrderId={workorder?.id}
        complementNumber={complement?.number}
        licensePlate={workorder?.vehicle?.licensePlate}
        complementIdentifier={complementIdentifier}
      />
    </>
  );
}
